<template>
  <label
    class="hs-date-input"
    :class="{ 'hs-date-input--disabled': disabled, 'hs-date-input--invalid': error }"
    @click="openPicker"
  >
    <span class="hs-date-input__text">
      <span v-if="label" class="hs-date-input__label">{{ label }}</span>
      <span class="hs-date-input__value">{{ displayValue }}</span>
    </span>
    <vue-feather type="calendar" size="15" class="hs-date-input__icon" />
    <input
      ref="input"
      class="hs-date-input__native"
      :value="modelValue"
      :type="type"
      :min="min || undefined"
      :max="max || undefined"
      :required="required"
      :disabled="disabled"
      @input="emitValue"
      @change="emitChange"
      @click.stop="openPicker"
    />
  </label>
</template>

<script>
function isValidDateString(value, type = "date") {
  if (typeof value !== "string" || !value) return false;
  if (type === "datetime-local") {
    const parsed = new Date(value);
    return !Number.isNaN(parsed.getTime());
  }
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return false;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  const normalized = [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, "0"),
    String(parsed.getDate()).padStart(2, "0"),
  ].join("-");
  return !Number.isNaN(parsed.getTime()) && normalized === value;
}

export default {
  name: "HsDateInput",
  props: {
    modelValue: { type: String, default: "" },
    type: {
      type: String,
      default: "date",
      validator: (value) => ["date", "datetime-local"].includes(value),
    },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "Select date" },
    hint: { type: String, default: "" },
    min: { type: String, default: "" },
    max: { type: String, default: "" },
    error: { type: String, default: "" },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "change"],
  computed: {
    displayValue() {
      if (!isValidDateString(this.modelValue, this.type)) return this.placeholder;
      if (this.type === "datetime-local") {
        return new Date(this.modelValue).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      const [year, month, day] = this.modelValue.split("-");
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
  methods: {
    openPicker(event) {
      if (this.disabled) return;
      const input = this.$refs.input;
      if (event?.target !== input) {
        event?.preventDefault?.();
      }
      input?.focus();
      input?.showPicker?.();
    },
    emitValue(event) {
      this.$emit("update:modelValue", event.target.value);
    },
    emitChange(event) {
      this.$emit("change", event.target.value);
    },
  },
};
</script>

<style scoped>
.hs-date-input {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 68%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  color: var(--hs-text, #111827);
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-height: 44px;
  min-width: 188px;
  overflow: hidden;
  padding: 7px 12px;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.hs-date-input:hover,
.hs-date-input:focus-within {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 40%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent);
  transform: translateY(-1px);
}

.hs-date-input--invalid {
  border-color: color-mix(in srgb, #ef4444 60%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.hs-date-input--disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.hs-date-input__text {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.hs-date-input__label {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.hs-date-input__value {
  color: var(--hs-text, #111827);
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.1;
}

.hs-date-input__icon {
  color: var(--hs-muted, #64748b);
  flex-shrink: 0;
}

.hs-date-input__native {
  appearance: none;
  background: transparent;
  border: 0;
  cursor: pointer;
  inset: 0;
  opacity: 0;
  outline: none;
  position: absolute;
  width: 100%;
}

.hs-date-input__native::-webkit-calendar-picker-indicator {
  cursor: pointer;
  height: 100%;
  inset: 0;
  opacity: 0;
  position: absolute;
  width: 100%;
}
</style>
